import { NgZone } from '@angular/core';
import decode from 'jwt-decode';
import moment from 'moment';
import { Observable } from 'rxjs';

import makeDebug from '../../../makeDebug';
import { TokenExpiredError } from './token-expired-error';
import { TokenMalformedError } from './token-malformed-error';

const TOKEN_EXPIRATION_THRESHOLD_IN_MS = 2 * 60 * 1000;

const debug = makeDebug('auth:auth');

export function verifyJWT(accessToken) {
  if (typeof accessToken !== 'string') {
    throw new Error('Token provided to verifyJWT is missing or not a string');
  }
  try {
    const payload = decode(accessToken);

    if (payloadIsValid(payload)) {
      return payload;
    }
  } catch (error) {
    throw new TokenMalformedError('Cannot decode malformed token.');
  }
  throw new TokenExpiredError('Invalid token: expired');
}

function payloadIsValid(payload) {
  return payload && (!payload.exp || payload.exp * 1000 > new Date().getTime());
}

function verifyJWTAboutToExpire(accessToken) {
  try {
    const payload = decode(accessToken);

    return moment(new Date(payload.exp * 1000)).diff(moment(), 'millisecond', true) <= TOKEN_EXPIRATION_THRESHOLD_IN_MS;
  } catch (error) {
    throw new TokenMalformedError('Cannot decode malformed token.');
  }
}

/**
 * Emits nothing and completes once the given token is about to expire.
 *
 * The timer handle is per subscription (#26615). It used to live in a module level variable, so a
 * second call cleared the first call's interval while its subscriber was still listening: that
 * subscriber's `complete` never fired and the token refresh for that flow was silently lost.
 * Unsubscribing now stops exactly the timer that belongs to the subscription.
 */
export function isTokenAboutToExpire(accessToken, ngZone: NgZone): Observable<boolean> {
  return new Observable(observer => {
    const interval = ngZone.runOutsideAngular(() =>
      setInterval(() => {
        try {
          if (verifyJWTAboutToExpire(accessToken)) {
            debug('accessToken expired');
            observer.complete();
          }
        } catch (error) {
          observer.error(error);
        }
      }, 2000)
    );

    // Runs on complete, on error and on unsubscribe.
    return () => global.clearInterval(interval);
  });
}
